import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Box,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { myTableStyles } from './MyTable.styles';
import { TableRowActions } from '../index';

// Extract the plain-text content from a rendered cell value for use as the
// tooltip title. Only scalar (string/number) values get a tooltip; complex
// React elements (chips, status badges, custom markup) are returned unwrapped.
const getTooltipTitle = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  return '';
};

// Wrap cell content in a Tooltip that only activates when the text actually
// overflows the column width. Uses scrollWidth vs clientWidth on the inner
// span to detect ellipsis truncation.
const TruncatedCell = ({ children, title }) => {
  const ref = React.useRef(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  const checkOverflow = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
  }, []);

  React.useEffect(() => {
    checkOverflow();
  });

  const inner = (
    <Box
      ref={ref}
      component="span"
      onMouseEnter={checkOverflow}
      sx={{
        display: 'block',
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );

  if (!title || !isOverflowing) {
    return inner;
  }

  return (
    <Tooltip title={title} placement="top" arrow>
      {inner}
    </Tooltip>
  );
};

const MyTable = ({
  columns = [],
  data = [],
  showActions = true,
  rowActions = [],
  onRowAction,
  onRowClick,
  getRowSx,
  onSort,
  ...props
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const totalFlex = columns.reduce((total, col) => total + (col.flex || 1), 0);

  return (
    <TableContainer component={Paper} sx={myTableStyles.tableContainer(theme)}>
      <Table {...props}>
        <TableHead sx={myTableStyles.tableHead(theme)}>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                key={index}
                align={column.align || 'left'}
                sx={{
                  ...myTableStyles.tableCell(theme),
                  width: column.flex
                    ? `${(column.flex / totalFlex) * 100}%`
                    : column.width
                      ? column.width
                      : `${100 / columns.length}%`,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: column.sortable ? 'pointer' : 'default',
                }}
                onClick={column.sortable && onSort ? () => onSort(column.field) : undefined}
              >
                {column.title}
              </TableCell>
            ))}
            {showActions && (
              <TableCell
                align="center"
                sx={{
                  ...myTableStyles.tableCell(theme),
                  width: '100px',
                  maxWidth: '100px',
                  minWidth: '100px',
                  paddingLeft: theme.spacing(0.5),
                  paddingRight: theme.spacing(0.5),
                  [theme.breakpoints.down('md')]: {
                    width: '80px',
                    maxWidth: '80px',
                    minWidth: '80px',
                    paddingLeft: theme.spacing(0.25),
                    paddingRight: theme.spacing(0.25),
                  },
                }}
              >
                {t('table.columns.actions')}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, rowIndex) => {
            const customRowSx = getRowSx ? getRowSx(row, rowIndex) : {};
            return (
            <TableRow
              key={rowIndex}
              sx={{
                ...myTableStyles.tableRow(theme),
                ...(onRowClick ? { cursor: 'pointer' } : {}),
                ...customRowSx,
                // Zebra striping (`:nth-of-type(odd)`) pseudo-class
                // specificity'i düz property'i ezdiği için getRowSx'in
                // backgroundColor'u odd satırlarda görünmüyordu. Aynı
                // selector altına customRowSx'i tekrar uygulayıp ezmeyi
                // bertaraf ediyoruz.
                '&:nth-of-type(odd)': {
                  ...myTableStyles.tableRow(theme)['&:nth-of-type(odd)'],
                  ...customRowSx,
                },
              }}
              onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
            >
              {columns.map((column, colIndex) => {
                const rawValue = row[column.field];
                const renderedValue = column.render
                  ? column.render(rawValue, row, rowIndex)
                  : rawValue;
                // Prefer the raw field value (usually the full, untruncated
                // text) as the tooltip title; fall back to the rendered value
                // when it is a plain string/number.
                const tooltipTitle =
                  getTooltipTitle(rawValue) || getTooltipTitle(renderedValue);
                return (
                  <TableCell
                    key={colIndex}
                    align={column.align || 'left'}
                    sx={{
                      ...myTableStyles.tableCell(theme),
                      width: column.flex
                        ? `${(column.flex / totalFlex) * 100}%`
                        : column.width
                          ? column.width
                          : `${100 / columns.length}%`,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <TruncatedCell title={tooltipTitle}>
                      {renderedValue}
                    </TruncatedCell>
                  </TableCell>
                );
              })}
              {showActions && (
                <TableCell
                  align="center"
                  sx={{
                    ...myTableStyles.tableCell(theme),
                    width: '100px',
                    maxWidth: '100px',
                    minWidth: '100px',
                    paddingLeft: theme.spacing(0.5),
                    paddingRight: theme.spacing(0.5),
                    position: 'sticky',
                    right: 0,
                    backgroundColor: theme.palette.background.paper,
                    zIndex: 1,
                    [theme.breakpoints.down('md')]: {
                      width: '80px',
                      maxWidth: '80px',
                      minWidth: '80px',
                      paddingLeft: theme.spacing(0.25),
                      paddingRight: theme.spacing(0.25),
                    },
                  }}
                >
                  <TableRowActions
                    row={row}
                    rowIndex={rowIndex}
                    actions={
                      typeof rowActions === 'function'
                        ? rowActions(row, rowIndex)
                        : rowActions
                    }
                    onAction={onRowAction}
                  />
                </TableCell>
              )}
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MyTable;
